import React from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { SectionHeader } from "../components/section-header";
import { SectionLoader } from "../components/section-loader";
import { EmptyState } from "@/components/shared/empty-state";
import { Briefcase, ArrowRight } from "lucide-react";

interface CareerOpportunitiesSectionProps {
  items: any[];
  isLoading: boolean;
}

export function CareerOpportunitiesSection({ items, isLoading }: CareerOpportunitiesSectionProps) {
  const displayItems = items.length > 0 ? items.slice(0, 3) : [];

  if (!displayItems.length && !isLoading) return null;

  return (
    <section className="border-b border-border" aria-labelledby="careers-heading">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
        <SectionHeader
          eyebrow="Careers"
          icon={Briefcase}
          title="Career Opportunities"
          href="/career"
        />
        {isLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-neutral-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : displayItems.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {displayItems.map((item: any) => (
              <Card key={item.id} className="group hover:border-neutral-300 transition-all duration-150">
                <CardContent className="p-5">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">{item.title}</CardTitle>
                    <CardDescription>{item.company} · {item.location}</CardDescription>
                  </CardHeader>
                  <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">{item.description}</p>
                  <div className="mt-4">
                    <Button variant="ghost" size="xs" asChild className="p-0 h-auto">
                      <Link href={`/career`}>
                        View position <ArrowRight className="ml-1 h-3 w-3" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <EmptyState icon={Briefcase} description="No career opportunities yet." />
        )}
      </div>
    </section>
  );
}